import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  Loader2, 
  BookOpen, 
  Calendar,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipSuccess = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [paymentType, setPaymentType] = useState(null);

  const sessionId = searchParams.get('session_id');
  const type = searchParams.get('type');

  useEffect(() => {
    if (sessionId && token) {
      setPaymentType(type);
      pollPaymentStatus(sessionId);
    } else {
      navigate('/membership');
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/subscription/checkout-status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-navy-50 dark:bg-navy-900 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-navy-100 dark:border-navy-700 dark:bg-navy-800">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-navy-900 dark:text-white mb-2">
              Processing Payment...
            </h2>
            <p className="text-navy-600 dark:text-navy-300">
              Please wait while we confirm your payment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'expired' || status === 'timeout') {
    return (
      <div className="min-h-screen bg-navy-50 dark:bg-navy-900 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-red-200 dark:border-red-800 dark:bg-navy-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">!</span>
            </div>
            <h2 className="font-heading text-xl font-semibold text-navy-900 dark:text-white mb-2">
              Payment Issue
            </h2>
            <p className="text-navy-600 dark:text-navy-300 mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We couldn\'t verify your payment. Please check your membership status.'}
            </p>
            <Button onClick={() => navigate('/membership')} className="bg-amber-600 hover:bg-amber-700">
              Go to Membership
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white dark:from-navy-900 dark:to-navy-800 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full border-emerald-200 dark:border-emerald-800 dark:bg-navy-800 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-white text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">
            {paymentType === 'initiation' 
              ? 'Initiation Complete!' 
              : 'Welcome to Atlas!'}
          </h1>
          <p className="text-emerald-100">
            {paymentType === 'initiation'
              ? 'Your $50 initiation payment was successful.'
              : 'Your membership is now active at $10/month.'}
          </p>
        </div>

        <CardContent className="p-6">
          {paymentType === 'initiation' ? (
            <>
              <div className="text-center mb-6">
                <Sparkles className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <h2 className="font-heading text-lg font-semibold text-navy-900 dark:text-white mb-2">
                  One More Step!
                </h2>
                <p className="text-navy-600 dark:text-navy-300">
                  Activate your monthly subscription to access all features.
                </p>
              </div>
              <Button 
                className="w-full h-12 bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/membership')}
              >
                Continue to Subscription
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="font-heading text-lg font-semibold text-navy-900 dark:text-white mb-2">
                  Your Career Transformation Starts Now
                </h2>
                <p className="text-navy-600 dark:text-navy-300">
                  You now have full access to the Atlas platform.
                </p>
              </div>

              <div className="space-y-3">
                <Button 
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/learning')}
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Enter Learning Suite
                </Button>
                <Button 
                  variant="outline"
                  className="w-full h-12 dark:border-navy-600 dark:text-white"
                  onClick={() => navigate('/career-chat')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Start Career Coaching
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipSuccess;
